import axios from 'axios';
import Chart from 'chart.js/auto';
import JustGage from 'justgage';
import React, { useEffect, useRef, useState } from 'react';
import OEECalculator from '../OEE/Oee';
import SevenSegmentBS from '../SevenSegmentStyle/SevenSegmentBS';

const OEE = () => {
  const [formData, setFormData] = useState({
    totalcount: '',
    scrapcount: '',
    otherbreaks: '',
  });
  const [results, setResults] = useState(null);
  const [machineTime, setMachineTime] = useState(0);
  const oeeChartRef = useRef(null);
  const oeeGaugeRef = useRef(null);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const totalProductionTime = 480; // Total production time in minutes
    const actualOperatingTime = 420 - parseFloat(formData.otherbreaks);
    const totalcount = parseFloat(formData.totalcount);
    const scrapcount = parseFloat(formData.scrapcount);
    const goodcount = totalcount - scrapcount;

    const availability = (actualOperatingTime / totalProductionTime) * 100;
    const quality = (goodcount / totalcount) * 100;

    try {
      const response = await axios.get('https://cmti-edge.online/foundry/php/machinedate.php');
      const machineTimeHours = response.data.machine_time_hours ;
      const machineTimeMinutes = machineTimeHours * 60;
      const performance = (machineTimeMinutes / actualOperatingTime) * 100 ;

      const oee = (availability * performance * quality) / 10000;
      const oeeLoss = 100 - oee;
      const downtimePercentage = ((60 + parseFloat(formData.otherbreaks)) / 420) * 100;
      const goodUnitsPercentage = (goodcount / totalcount) * 100;
      const badUnitsPercentage = 100 - goodUnitsPercentage;

      setResults({
        oee,
        availability,
        performance,
        quality,
        oeeLoss,
        downtimePercentage,
        goodUnitsPercentage,
        badUnitsPercentage
      });

      updateCharts(oee, availability, performance, quality, oeeLoss, downtimePercentage, goodUnitsPercentage, badUnitsPercentage);

      // Send data to backend
      await axios.post('https://cmti-edge.online/foundry/php/input.php', {
        totalcount,
        scrapcount,
        availability,
        performance,
        quality,
        oee,
      });

    } catch (error) {
      console.error('Error in OEE calculation:', error);
    }
  };

  const updateCharts = (oee, availability, performance, quality, oeeLoss, downtimePercentage, goodUnitsPercentage, badUnitsPercentage) => {
    updateGauge('availabilityGauge', availability, '#4CAF50');
    updateGauge('performanceGauge', performance, '#2196F3');
    updateGauge('qualityGauge', quality, '#FF5722');

    if (oeeGaugeRef.current) {
      oeeGaugeRef.current.refresh(oee);
    } else {
      oeeGaugeRef.current = new JustGage({
        id: "oeeGauge",
        value: oee,
        min: 0,
        max: 100,
        gaugeWidthScale: 0.8,
        counter: true,
        hideInnerShadow: true,
        customSectors: [
          { color: "#FF0000", lo: 0, hi: 60 },
          { color: "#FFA500", lo: 60, hi: 85 },
          { color: "#00FF00", lo: 85, hi: 100 }
        ],
        levelColorsGradient: false,
        valueFontColor: "#FFFFFF",
        hideMinMax: false,
        title: "OEE",
        label: "%",
        levelColors: ["purple"]
      });
    }

    updatePieChart(oee, downtimePercentage, goodUnitsPercentage, badUnitsPercentage, oeeLoss);
  };

  const updateGauge = (gaugeId, percentage, color) => {
    const gauge = document.getElementById(gaugeId);
    const circle = gauge.querySelector('.foreground-circle');
    const resultValue = gauge.querySelector('.result-value');

    const circumference = 2 * Math.PI * 65;
    const dashOffset = circumference * (1 - (percentage / 100));

    circle.style.strokeDasharray = `${circumference} ${circumference}`;
    circle.style.strokeDashoffset = dashOffset;
    circle.style.stroke = color;

    resultValue.textContent = `${percentage.toFixed(2)}%`;
  };

  const updatePieChart = (oee, downtime, goodUnits, badUnits, oeeLoss) => {
    if (oeeChartRef.current) {
      oeeChartRef.current.destroy();
    }

    const ctx = document.getElementById('oeeLossChart').getContext('2d');
    oeeChartRef.current = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['OEE', 'Downtime', 'Good Units', 'Bad Units', 'OEE Loss'],
        datasets: [{
          data: [oee, downtime, goodUnits, badUnits, oeeLoss],
          backgroundColor: ['#FF0000', '#0000FF', '#FFA500', '#cc0066', '#33cc33'],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: 'white'
            }
          },
          title: {
            display: true,
            text: 'OEE and Production Breakdown',
            color: 'white'
          }
        }
      }
    });
  };

  useEffect(() => {
    const fetchMachineTime = async () => {
      try {
        const response = await axios.get('https://cmti-edge.online/foundry/php/machinedate.php');
        
        // Get the raw value from the response
        let rawTime = response.data.machine_time_minutes;
        
        // Convert to number if it's a string
        const timeValue = typeof rawTime === 'string' ? parseFloat(rawTime) : rawTime;
        
        // Validate the value is a number
        if (isNaN(timeValue)) {
          console.error('Invalid machine time received:', rawTime);
          setMachineTime(1); // Set to minimum if invalid
          return;
        }
        
        // Check if value is less than 1 (minimum)
        if (timeValue < 1) {
          console.warn('Machine time less than minimum, setting to 1:', timeValue);
          setMachineTime(1);
          return;
        }
        
        // Check if value is greater than 99999 (5 digits maximum)
        if (timeValue > 99999) {
          console.warn('Machine time exceeds maximum, setting to 99999:', timeValue);
          setMachineTime(99999);
          return;
        }
        
        // If within range, set the value (and ensure it's an integer if needed)
        setMachineTime(Math.round(timeValue));
        
      } catch (error) {
        console.error('Error fetching machine time:', error);
        setMachineTime(1); // Set to minimum if error occurs
      }
    };
  
    fetchMachineTime();
    const interval = setInterval(fetchMachineTime, 60000); // Update every minute
  
    return () => clearInterval(interval);
  }, []);


  return (
    <div id="oee" className=" bg-gray-800 text-white p-5">
      <div className="flex flex-col lg:flex-row justify-around items-center mb-5 w-full">
        <ParameterDisplay title="Planned Production Time" value="480  minutes" />
        <ParameterDisplay title="Planned Break Time" value="60 minutes" />
        <ParameterDisplay title="Machine Operating Time" value={`${machineTime} minutes`} />
      </div>
      <div className="flex flex-col justify-around items-center">
        <div className="w-full  mb-5">
          <h3 className="text-center text-xl font-bold mb-5">OEE Calculator</h3>
          <OEECalculator />
        </div>
        <div className="w-full lg:w-1/2 flex flex-col items-center">
          <div id="oeeGauge" className="w-full h-48 mb-5"></div>
          <canvas id="oeeLossChart" className="w-full h-64"></canvas>
        </div>
      </div>
    </div>
  );
};

const ParameterDisplay = ({ title, value }) => (
  <div className="flex flex-col items-center  bg-gray-700 p-5 w-96 md:w-96 lg:w-[30rem] gap-5 rounded-lg shadow-lg transform hover:scale-105 transition-transform duration-200 ease-in-out mb-5 lg:mb-0">
    <h2 className="text-xl font-bold py-4">{title}</h2>
    <SevenSegmentBS value={value  } className="w-full py-4 px-4" />
  </div>
);

export default OEE;
